import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpEvent } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { ChatService } from './chat.service';

// Matches DocumentUploadService::MAX_TOTAL_FILE_BYTES: nginx 20 MiB minus multipart reserve.
export const DOCUMENT_UPLOAD_MAX_TOTAL_FILE_BYTES = 19 * 1024 * 1024;
export const DOCUMENT_UPLOAD_MAX_FILES = 10;
export const DOCUMENT_UPLOAD_MAX_FILE_BYTES = 15 * 1024 * 1024;
// Mirrors DocumentUploadService::ALLOWED_MIME_BY_EXTENSION, limited to the types browsers report.
const ALLOWED_MIME_BY_EXTENSION: Record<string, string[]> = {
  pdf: ['application/pdf'],
  png: ['image/png'],
  jpg: ['image/jpeg'],
  jpeg: ['image/jpeg'],
  gif: ['image/gif'],
  webp: ['image/webp'],
  tif: ['image/tiff'],
  tiff: ['image/tiff'],
  txt: ['text/plain'],
  eml: ['message/rfc822'],
  xls: ['application/vnd.ms-excel'],
  xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  docx: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
};
export const DOCUMENT_UPLOAD_ACCEPT = Object.keys(ALLOWED_MIME_BY_EXTENSION).map((extension) => `.${extension}`).join(',');

/** Client-side pre-check before upload: returns the accepted new files and the first rejection message. */
export function selectDocumentUploadFiles(
  selected: File[],
  incoming: File[],
  t: (key: string) => string,
): { files: File[]; error: string | null } {
  const files: File[] = [];
  const errors: string[] = [];

  for (const file of incoming) {
    const nextFiles = [...selected, ...files];
    if (nextFiles.length >= DOCUMENT_UPLOAD_MAX_FILES) {
      errors.push(t('documentUploadModal.tooManyFiles'));
      break;
    }

    const validationError = documentUploadFileError(file);
    if (validationError) {
      errors.push(`${file.name}: ${t(validationError)}`);
      continue;
    }

    if (nextFiles.reduce((sum, current) => sum + current.size, 0) + file.size > DOCUMENT_UPLOAD_MAX_TOTAL_FILE_BYTES
      || new TextEncoder().encode([...nextFiles.map((current) => current.name), file.name].join('')).length > 32 * 1024) {
      errors.push(t('documentUploadModal.totalTooLarge'));
      continue;
    }

    files.push(file);
  }

  return { files, error: errors[0] ?? null };
}

function documentUploadFileError(file: File): string | null {
  if (file.size < 1) {
    return 'documentUploadModal.emptyFile';
  }

  if (file.size > DOCUMENT_UPLOAD_MAX_FILE_BYTES) {
    return 'documentUploadModal.fileTooLarge';
  }

  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  const allowedMimes = Object.hasOwn(ALLOWED_MIME_BY_EXTENSION, extension) ? ALLOWED_MIME_BY_EXTENSION[extension] : null;
  // An empty type means the browser could not detect it; the backend still enforces the detected MIME type.
  if (!allowedMimes || (file.type !== '' && !allowedMimes.includes(file.type))) {
    return 'documentUploadModal.invalidFileType';
  }

  return null;
}

export interface DocumentUploadMetadata {
  category: string;
  reason: string;
  comment?: string;
  modalNonce?: string;
  uploadContext?: 'chat_modal' | 'quick_action_document_upload';
  quickActionUploadToken?: string;
}

export interface DocumentUploadResult {
  uploads: Array<{
    original_name: string;
    upload_index: number;
    upload_id: string;
    size: number;
    extension: string;
    mime_type: string;
    category: string;
    reason: string;
    status: string;
  }>;
  file_count: number;
}

export interface DocumentUploadApiResponse {
  success: boolean;
  data: DocumentUploadResult;
}

@Injectable({ providedIn: 'root' })
export class DocumentUploadService {
  private readonly http = inject(HttpClient);
  private readonly chatService = inject(ChatService);
  private readonly uploadUrl = '/api/documents/upload';

  upload(files: File[], metadata: DocumentUploadMetadata): Observable<HttpEvent<DocumentUploadApiResponse>> {
    const sessionId = this.chatService.getSessionId();
    const formData = new FormData();
    formData.append('session_id', sessionId ?? '');
    formData.append('category', metadata.category);
    formData.append('reason', metadata.reason);
    formData.append('comment', metadata.comment ?? '');
    formData.append('upload_context', metadata.uploadContext ?? 'chat_modal');
    if (metadata.modalNonce) {
      formData.append('modal_nonce', metadata.modalNonce);
    }
    if (metadata.quickActionUploadToken) {
      formData.append('quick_action_upload_token', metadata.quickActionUploadToken);
    }
    for (const file of files) {
      formData.append('files[]', file, file.name);
    }

    // Bound text/filenames well below the reserved 1 MiB, including header escaping.
    let textBytes = 0;
    formData.forEach((value) => {
      textBytes += new TextEncoder().encode(typeof value === 'string' ? value : value.name).length;
    });
    if (files.reduce((sum, file) => sum + file.size, 0) > DOCUMENT_UPLOAD_MAX_TOTAL_FILE_BYTES
      || textBytes > 64 * 1024 || files.length > 10) {
      return throwError(() => new Error('Document upload request exceeds its envelope.'));
    }

    return this.http.post<DocumentUploadApiResponse>(this.uploadUrl, formData, {
      observe: 'events',
      reportProgress: true,
    });
  }
}
